import { useFieldArray } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { TurnType } from '@db8/shared'
import { Accordion } from '@/components/ui/accordion'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { SortableStageItem } from '@/components/sequence/create-stage'
import { formSchema } from './page'
import { z } from 'zod'
import { Form } from '@/components/ui/form'

export const DebateBuilder = ({ form, onNext, controlBtns, setIsAIProcessing }) => {
  const { control } = form
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'stages',
    shouldUnregister: true,
  })

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  )

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = fields.findIndex((field) => field.id === active.id)
    const newIndex = fields.findIndex((field) => field.id === over.id)

    move(oldIndex, newIndex)

    const updatedStages = form.getValues('stages') || []

    let affirmativeCount = 0
    let negativeCount = 0

    updatedStages.forEach((stage, index) => {
      if (stage.type === TurnType.Affirmative) {
        affirmativeCount++
        form.setValue(`stages.${index}.abbreviation`, `${affirmativeCount}A`)
      } else if (stage.type === TurnType.Negative) {
        negativeCount++
        form.setValue(`stages.${index}.abbreviation`, `${negativeCount}N`)
      } else if (stage.type === TurnType.Preperatory) {
        form.setValue(`stages.${index}.abbreviation`, 'PREP')
      } else if (stage.type === TurnType.AffirmativeCrossNegative) {
        form.setValue(`stages.${index}.abbreviation`, `(AxN)`)
      } else if (stage.type === TurnType.NegativeCrossAffirmative) {
        form.setValue(`stages.${index}.abbreviation`, `(NxA)`)
      }
    })
  }

  const handleAddStage = (e) => {
    e.preventDefault()

    const affirmativeCount = fields.filter(
      // @ts-ignore
      (f) => f.type === TurnType.Affirmative,
    ).length
    const negativeCount = fields.filter(
      //@ts-ignore
      (f) => f.type === TurnType.Negative,
    ).length

    const newStage = {
      title: 'New Stage',
      duration: 1,
      type: TurnType.Affirmative,
      abbreviation: '',
    }

    if (newStage.type === TurnType.Affirmative) newStage.abbreviation = `${affirmativeCount + 1}A`

    append(newStage)
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    if (onNext) onNext(values)
  }

  return (
    <div className="h-full flex flex-col py-6 sm:px-6 md:px-8 overflow-y-auto overflow-x-hidden noScrollbar">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, () => {})}
          className="flex flex-col h-full gap-4"
        >
          <div className="mb-2">
            <h2 className="text-xl sm:text-2xl font-bold mb-1">Debate Sequence</h2>
            <p className="text-sm text-gray-500 max-w-xl">
              Add, remove, edit, and sort your debate stages. Define the stage name, duration, and
              type.
            </p>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={fields.map((field) => field.id)}
              strategy={verticalListSortingStrategy}
            >
              <Accordion type="multiple" className="space-y-2">
                {fields.map((field, index) => (
                  <SortableStageItem
                    key={field.id}
                    id={field.id}
                    form={form}
                    field={field}
                    index={index}
                    remove={remove}
                  />
                ))}
              </Accordion>
            </SortableContext>
          </DndContext>

          <div className="my-4 flex justify-center">
            <Button onClick={handleAddStage} variant="outline">
              Add Stage
            </Button>
          </div>

          <div className="mt-auto w-full">{controlBtns}</div>
        </form>
      </Form>
    </div>
  )
}
